import React, { useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  success: boolean; // true for success, false for error
  message?: string; // Optional custom message
  autoCloseDelay?: number; // Auto-close after this many milliseconds (default 3000ms = 3 seconds)
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ open, onClose, success, message, autoCloseDelay = 4000 }) => {
  // Auto-close after specified delay
  useEffect(() => {
    if (open) {
      console.log("🎉 PaymentDialog opened:", { success, message });
      const timer = setTimeout(() => {
        console.log("⏰ PaymentDialog auto-closing after", autoCloseDelay, "ms");
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [open, autoCloseDelay, onClose]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing when clicking outside (backdrop) - only allow closing from X button
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      sx={{
        zIndex: 9999, // Very high z-index to ensure it appears above everything
        "& .MuiDialog-container": {
          zIndex: 9999,
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: PureLightThemeCaaS.colors.alpha.black[100],
          color: PureLightThemeCaaS.colors.alpha.white[100],
          boxShadow: "4px 4px 12px rgba(0,0,0,0.25)",
          borderRadius: "8px",
          border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          minWidth: "300px",
          maxWidth: "500px",
          zIndex: 9999,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            position: "fixed",
            backdropFilter: "blur(6px) !important",
            WebkitBackdropFilter: "blur(1px) !important",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 9998, // Just below the dialog
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          color: success ? "#36960c" : "#d32f2f",
          fontWeight: "bold",
          fontSize: "16px",
          fontFamily: '"Inter", sans-serif',
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 1,
          pb: 0,
          pt: 0.5,
          pl: 1,
        }}
      >
        {success ? "Payment Successful" : "Payment Cancelled"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: PureLightThemeCaaS.colors.alpha.white[100],
            "&:hover": {
              backgroundColor: PureLightThemeCaaS.colors.alpha.black[50],
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
        {success && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 52,
                color: "#36960c",
              }}
            />
          </Box>
        )}
        <Typography
          sx={{
            color: PureLightThemeCaaS.colors.alpha.white[100],
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "1.5",
            fontFamily: '"Inter", sans-serif',
            textAlign: "center",
          }}
        >
          {message || (success ? "Your payment has been processed successfully." : "Your payment could not be processed. Please try again.")}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
